import { UserRole } from "enums";
import { BudgetStatus } from "interfaces/budget";

export const FIRST_ACCESS_WHATSAPP_RESEND_LABEL =
  "Reenviar mensagem de primeiro acesso via WhatsApp";

export const FIRST_ACCESS_WHATSAPP_RESEND_UNAVAILABLE_LABEL =
  "Reenvio indisponível para este status";

export const FIRST_ACCESS_WHATSAPP_RESEND_ELIGIBLE_STATUSES: readonly number[] =
  [
    BudgetStatus.AGUARDANDO_PAGAMENTO,
    BudgetStatus.CONFIRMACAO_CLIENTE,
    BudgetStatus.AUTORIZADO,
    BudgetStatus.PROCESSADO,
    BudgetStatus.VALIDADO_SEM_CRITICA,
    BudgetStatus.ASSINADO_CLIENTE,
    BudgetStatus.PAGO_CLIENTE,
  ];

const ALLOWED_PERMISSIONS = new Set<string>([
  UserRole.Admin,
  UserRole.ReportsFull,
]);

export type FirstAccessWhatsappResendResponse = {
  budgetId?: string;
  nuControle?: string;
  messageSent?: boolean;
  status?: string;
  correlationId?: string;
  detail?: string;
  alreadySent?: boolean;
};

type ErrorLike = {
  statusCode?: number;
  response?: {
    status?: number;
  };
};

export const normalizeBudgetStatus = (status: unknown): number | null => {
  if (typeof status === "number" && Number.isFinite(status)) {
    return status;
  }

  if (typeof status === "string" && status.trim() !== "") {
    const parsed = Number(status);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

export const isFirstAccessWhatsappResendEligible = (
  status: unknown,
): boolean => {
  const normalized = normalizeBudgetStatus(status);
  return (
    normalized !== null &&
    FIRST_ACCESS_WHATSAPP_RESEND_ELIGIBLE_STATUSES.includes(normalized)
  );
};

export const canUseFirstAccessWhatsappResend = (
  permissions?: unknown,
): boolean => {
  if (Array.isArray(permissions)) {
    return permissions.some((permission) =>
      ALLOWED_PERMISSIONS.has(String(permission)),
    );
  }

  return ALLOWED_PERMISSIONS.has(String(permissions));
};

export const getFirstAccessWhatsappResendSuccessMessage = (
  response?: FirstAccessWhatsappResendResponse,
): string => {
  if (response?.alreadySent) {
    return "A mensagem já havia sido enviada para este orçamento e foi reenviada com sucesso.";
  }

  if (response?.status === "resent") {
    return "Mensagem de primeiro acesso reenviada com sucesso.";
  }

  return "Mensagem de primeiro acesso enviada com sucesso.";
};

export const getFirstAccessWhatsappResendErrorMessage = (
  error?: unknown,
): string => {
  const status =
    typeof error === "number"
      ? error
      : ((error as ErrorLike | undefined)?.statusCode ??
        (error as ErrorLike | undefined)?.response?.status);

  switch (status) {
    case 403:
      return "Você não tem permissão para reenviar a mensagem deste orçamento.";
    case 404:
      return "Orçamento não encontrado.";
    case 409:
      return "Não foi possível reenviar a mensagem para este orçamento no status atual.";
    case 422:
      return "Não foi possível enviar a mensagem porque faltam informações obrigatórias no orçamento.";
    case 502:
      return "Não foi possível enviar a mensagem no momento. Tente novamente mais tarde.";
    default:
      return "Ocorreu um erro inesperado ao tentar reenviar a mensagem.";
  }
};
